import { isEqual } from 'lodash';
import { twMerge } from 'tailwind-merge';
import { useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import { Form, Formik, FormikValues } from 'formik';

import { maskStyle } from 'utils';
import { Background } from 'components';

import { Icon } from '../icons';

import { AuthLayoutProps } from './types';

const authLogoUrl = '/logo.png';

export function AuthLayout<Value extends FormikValues>({
  title,
  children,
  onSubmit,
  className,
  logoHidden,
  initialValues,
  lowerTitleSize,
  wrapperClassName,
  containerClassName,
  ...formikProps
}: AuthLayoutProps<Value>) {
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const valuesRef = useRef<Value>(initialValues);

  const resetMessages = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const onChangeValues = (values: Value) => {
    const isChanged = !isEqual(values, valuesRef.current);
    valuesRef.current = values;
    if (isChanged) {
      resetMessages();
    }
  };

  const renderedMessage = errorMessage || successMessage;

  return (
    <div
      className={twMerge(
        'flex min-h-screen w-screen justify-center overflow-y-auto py-12 md:pt-30',
        className
      )}
    >
      <Background className="z-0" />
      <div
        className={twMerge(
          'relative mx-6 flex h-fit w-106.5 min-w-0 flex-col gap-6 md:gap-10 lg:justify-center',
          wrapperClassName
        )}
      >
        {!logoHidden && (
          <div className="flex justify-center gap-4">
            <div className="w-47 md:w-67">
              <img
                src={authLogoUrl}
                alt="MIVAX Admin"
                className="h-auto w-full object-contain"
              />
            </div>
          </div>
        )}

        <div className="bg-white-primary shadow-primary flex flex-wrap rounded-3xl p-4 md:rounded-4xl md:p-6">
          <div className={twMerge('flex w-full flex-col', containerClassName)}>
            <Typography
              variant="h1"
              className="mb-6 text-center"
              sx={{
                fontSize: lowerTitleSize ? '20px' : '40px',
                lineHeight: lowerTitleSize ? '32px' : '48px',
              }}
            >
              {title}
            </Typography>

            {renderedMessage && (
              <div
                className={twMerge(
                  'mb-6 flex items-center gap-2 rounded-3xl border-[1.5px] px-[14.5px] py-[10.5px] md:mb-4',
                  !!successMessage && 'border-greenlight-primary',
                  !!errorMessage && 'border-red-primary-primary'
                )}
              >
                {errorMessage ? (
                  <Icon.Info
                    width={20}
                    height={20}
                    className="[&_path]:fill-red-primary-primary shrink-0 rotate-180"
                  />
                ) : (
                  <div
                    style={maskStyle('/images/check-circle.svg')}
                    className="bg-greenlight-primary h-5 w-5 shrink-0"
                  />
                )}
                <Typography>{renderedMessage}</Typography>
              </div>
            )}

            <div className="flex w-full items-center justify-center">
              <Formik<Value>
                {...formikProps}
                initialValues={initialValues}
                onSubmit={async (values) => {
                  resetMessages();
                  const result = onSubmit(values);
                  if (result instanceof Promise) {
                    const promiseResult = await result;
                    if (promiseResult && typeof promiseResult === 'object') {
                      if ('successMessage' in promiseResult) {
                        setSuccessMessage(promiseResult.successMessage);
                      }
                      if ('errorMessage' in promiseResult) {
                        setErrorMessage(promiseResult.errorMessage);
                      }
                    }
                    return promiseResult;
                  } else {
                    return result;
                  }
                }}
              >
                {(props) => {
                  const render =
                    typeof children === 'function'
                      ? children(props, {
                          resetMessages,
                          setErrorMessage,
                          setSuccessMessage,
                        })
                      : children;

                  onChangeValues(props.values);

                  return (
                    <div className="w-full">
                      <Form>{render}</Form>
                    </div>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
